import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { walletAPI } from '../services/api';
import { TransactionForm } from '../components/Wallet';
import './WalletTransaction.css';

function WalletTransaction() {
  const { walletId, type } = useParams(); // type: 'deposit', 'withdraw'
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [wallet, setWallet] = useState(null);
  const [availableWallets, setAvailableWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Determine transaction type from URL or params
  const getTransactionType = () => {
    // Check if URL path contains 'transfer'
    if (location.pathname.includes('/transfer')) {
      return 'transfer';
    }
    return type || searchParams.get('type') || 'deposit';
  };
  
  const transactionType = getTransactionType();
  const fromWalletId = searchParams.get('from');
  const toWalletId = searchParams.get('to');

  useEffect(() => {
    loadData();
  }, [walletId, transactionType]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const promises = [];

      // Load specific wallet if walletId provided
      if (walletId) {
        promises.push(walletAPI.getById(walletId));
      }

      // Always load available wallets for transfers
      promises.push(walletAPI.getAll({ active: true }));

      const results = await Promise.all(promises);

      if (walletId) {
        setWallet(results[0].data.data);
        setAvailableWallets(results[1].data.data?.wallets || []);
      } else {
        setAvailableWallets(results[0].data.data?.wallets || []);
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (transactionData) => {
    try {
      setProcessing(true);
      setError(null);
      setSuccess(null);

      let response;
      
      switch (transactionType) {
        case 'deposit':
          response = await walletAPI.deposit(
            walletId,
            transactionData.amount,
            transactionData.description,
            transactionData.metadata
          );
          break;
          
        case 'withdraw':
          response = await walletAPI.withdraw(
            walletId,
            transactionData.amount,
            transactionData.description,
            transactionData.metadata
          );
          break;
          
        case 'transfer':
          response = await walletAPI.transfer(
            transactionData.fromWalletId,
            transactionData.toWalletId,
            transactionData.amount,
            transactionData.description,
            transactionData.metadata
          );
          break;
          
        default:
          throw new Error('Invalid transaction type');
      }

      const transaction = response.data.data;
      setSuccess({
        message: 'Transaction completed successfully!',
        transaction
      });

      // Navigate to wallet details after a short delay
      setTimeout(() => {
        if (transactionType === 'transfer') {
          navigate('/wallets');
        } else {
          navigate(`/wallets/${walletId}`);
        }
      }, 2000);

    } catch (err) {
      console.error('Error processing transaction:', err);
      setError(err.response?.data?.message || 'Failed to process transaction');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    if (transactionType === 'transfer') {
      navigate('/wallets');
    } else if (walletId) {
      navigate(`/wallets/${walletId}`);
    } else {
      navigate('/wallets');
    }
  };

  const getPageTitle = () => {
    switch (transactionType) {
      case 'deposit':
        return wallet ? `Deposit to ${wallet.ownerName}` : 'Make Deposit';
      case 'withdraw':
        return wallet ? `Withdraw from ${wallet.ownerName}` : 'Make Withdrawal';
      case 'transfer':
        return 'Transfer Funds';
      default:
        return 'Transaction';
    }
  };

  const getBackLink = () => {
    if (transactionType === 'transfer') {
      return '/wallets';
    } else if (walletId) {
      return `/wallets/${walletId}`;
    } else {
      return '/wallets';
    }
  };

  if (loading) {
    return (
      <div className="wallet-transaction">
        <div className="transaction-loading">
          <div className="loading-spinner"></div>
          <p>Loading transaction form...</p>
        </div>
      </div>
    );
  }

  if (error && !wallet && !availableWallets.length) {
    return (
      <div className="wallet-transaction">
        <div className="transaction-error">
          <h2>Error Loading Transaction Form</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/wallets')} className="back-to-wallets-button">
            ← Back to Wallets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-transaction">
      <header className="transaction-header">
        <div className="header-navigation">
          <button onClick={handleCancel} className="back-button">
            ← Back
          </button>
        </div>
        <h1>{getPageTitle()}</h1>
      </header>

      {error && (
        <div className="transaction-error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <strong>Transaction Failed</strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="transaction-success-message">
          <div className="success-icon">✅</div>
          <div className="success-content">
            <strong>Transaction Successful!</strong>
            <p>{success.message}</p>
            <p className="redirect-message">
              Redirecting you back to wallet details...
            </p>
          </div>
        </div>
      )}

      {!success && (
        <div className="transaction-form-container">
          <TransactionForm
            type={transactionType}
            walletId={walletId || fromWalletId || toWalletId}
            availableWallets={availableWallets}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={processing}
            disabled={processing}
          />
        </div>
      )}

      {/* Wallet Info Sidebar */}
      {wallet && transactionType !== 'transfer' && (
        <div className="wallet-info-sidebar">
          <h3>Wallet Information</h3>
          <div className="wallet-summary">
            <div className="wallet-owner">
              <div className="owner-icon">
                {wallet.ownerType === 'member' ? '👤' : '🏌️'}
              </div>
              <div className="owner-details">
                <div className="owner-name">{wallet.ownerName}</div>
                <div className="owner-type">
                  {wallet.ownerType.charAt(0).toUpperCase() + wallet.ownerType.slice(1)} Wallet
                </div>
              </div>
            </div>
            
            <div className="balance-info">
              <div className="balance-label">Current Balance</div>
              <div className="balance-amount">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(wallet.balance)}
              </div>
            </div>

            <div className="wallet-status">
              <span className={`status-badge ${wallet.isActive ? 'active' : 'inactive'}`}>
                {wallet.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WalletTransaction;